/**
 * SetoutTool — three-phase field workflow.
 *
 * Phase 1 · DESIGN  — arrow builder (↑ ↓ ← → + mm distance) draws the
 *                     shape on the tablet. A always sits at (0,0).
 * Phase 2 · ESTABLISH — shoot Point A with the prism (locks anchor),
 *                       then aim the laser in the direction of B and
 *                       tap "Lock A→B direction" (locks rotation).
 *                       Alternative: physically shoot B too.
 * Phase 3 · LAYOUT   — every design corner now has a real-world xyz +
 *                       aim Hz/V. Tap ◎ to laser-aim, ✓ to mark,
 *                       "Verify with prism" to shoot & check residual.
 *
 * All coordinates are station-local (dx/dy/dz from scope pivot) so
 * the instrument's on-keypad world coords don't matter.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Crosshair, Trash, Aperture, RotateCcw,
  Undo2, Flag, CheckCircle2, X, ArrowUp, ArrowDown, ArrowRight,
  Target as TargetIcon,
} from "lucide-react";
import { api, station } from "../lib/api";
import { usePrismMode } from "../lib/prism-mode";
import { ToolSplitLayout } from "../components/ToolSplitLayout";
import { PrintReportShell, PrintReportButton } from "../components/PrintReportShell";
import { AimPad } from "../components/AimPad";
import SetoutGuided from "./SetoutGuided";

const soApi = {
  list: () => api.get("/setout/sessions").then((r) => r.data),
  create: (b) => api.post("/setout/sessions", b).then((r) => r.data),
  get: (id) => api.get(`/setout/sessions/${id}`).then((r) => r.data),
  del: (id) => api.delete(`/setout/sessions/${id}`).then((r) => r.data),
  tol: (id, tolerance_mm) => api.patch(`/setout/sessions/${id}/tolerance`, { tolerance_mm }).then((r) => r.data),
  replaceDesign: (id, corners) => api.put(`/setout/sessions/${id}/design`, { corners }).then((r) => r.data),
  setAnchor: (id, b) => api.post(`/setout/sessions/${id}/anchor`, b).then((r) => r.data),
  clearAnchor: (id) => api.delete(`/setout/sessions/${id}/anchor`).then((r) => r.data),
  setOrient: (id, b) => api.post(`/setout/sessions/${id}/orient`, b).then((r) => r.data),
  setOrientMeasure: (id, b) => api.post(`/setout/sessions/${id}/orient/measure`, b).then((r) => r.data),
  clearOrient: (id) => api.delete(`/setout/sessions/${id}/orient`).then((r) => r.data),
  markTarget: (id, cid, marked) => api.patch(`/setout/sessions/${id}/targets/${cid}/marked`, { marked }).then((r) => r.data),
  verifyTarget: (id, cid, xyz) => api.post(`/setout/sessions/${id}/targets/${cid}/verify`, xyz).then((r) => r.data),
  captureActual: (id, cid, xyz) => api.post(`/setout/sessions/${id}/targets/${cid}/actual`, xyz).then((r) => r.data),
  clearActual: (id, cid) => api.delete(`/setout/sessions/${id}/targets/${cid}/actual`).then((r) => r.data),
  log: (id, kind, data) => api.post(`/setout/sessions/${id}/log`, { kind, data }).then((r) => r.data).catch(() => null),
  // Any-plane set-out
  addPlaneShot: (id, b) => api.post(`/setout/sessions/${id}/plane-shots`, b).then((r) => r.data),
  deletePlaneShot: (id, idx) => api.delete(`/setout/sessions/${id}/plane-shots/${idx}`).then((r) => r.data),
  setPlaneMode: (id, mode) => api.patch(`/setout/sessions/${id}/plane-mode`, { mode }).then((r) => r.data),
  clearPlane: (id) => api.delete(`/setout/sessions/${id}/plane`).then((r) => r.data),
};

const rad2deg = (r) => ((r * 180) / Math.PI + 360) % 360;
const CORNER_LABELS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const localToAim = (p) => {
  const horiz = Math.hypot(p.world_x, p.world_y);
  let hz = Math.atan2(p.world_x, p.world_y);
  if (hz < 0) hz += 2 * Math.PI;
  const v = horiz < 1e-9 && Math.abs(p.world_z) < 1e-9
    ? Math.PI / 2
    : Math.atan2(horiz, p.world_z);
  return { hz, v };
};

// Shortest-signed angular distance between two radians (handles the
// 0 / 2π wrap-around). Used by the closed-loop precise aim loop.
const angleDiff = (a, b) => {
  let d = (a - b) % (2 * Math.PI);
  if (d > Math.PI) d -= 2 * Math.PI;
  if (d < -Math.PI) d += 2 * Math.PI;
  return d;
};

/**
 * Closed-loop precise aim with damped correction.
 *
 * The Leica motor drive on this instrument overshoots commanded angles
 * by ~8–15% and has a small dead-zone, which made a naive gain-1
 * feedback loop oscillate. This loop uses a DAMPED gain (0.5) so each
 * iteration removes half the residual, converging monotonically
 * instead of hunting.
 *
 * Strategy:
 *   1. Command the target once and settle (this is the big move).
 *   2. Read where scope actually landed.
 *   3. Compute the miss, add gain·miss to the target, command again.
 *   4. Repeat until within tolRad or maxIter used up.
 *
 * With gain 0.5 the residual halves each pass in the ideal case, so
 * a ~25 mrad initial miss reaches ~0.2 mrad (sub-mm at 1.5 m) in 6-7
 * iterations. If the miss dips below the motor's own dead-zone the
 * loop just returns (further commands wouldn't move the head).
 */
const preciseAim = async (hzTarget, vTarget, {
  tolRad = 3.0e-4,          // ≈ 0.5 mm at 1.5 m
  maxIter = 8,
  settleMs = 400,
  gain = 0.5,
  deadZoneRad = 1.5e-4,     // don't bother commanding sub-motor-step deltas
} = {}) => {
  let cmdHz = hzTarget;
  let cmdV = vTarget;
  let last = null;
  let dHz = 0, dV = 0;
  let iter = 0;
  const trace = [];
  for (iter = 0; iter < maxIter; iter++) {
    await station.turn({ hz: cmdHz, v: cmdV });
    await new Promise((r) => setTimeout(r, settleMs));
    last = await station.state().catch(() => null);
    if (!last || last.hz == null || last.v == null) break;
    dHz = angleDiff(hzTarget, last.hz);
    dV = angleDiff(vTarget, last.v);
    trace.push({
      iter: iter + 1,
      cmd_hz: +cmdHz.toFixed(6),
      cmd_v: +cmdV.toFixed(6),
      actual_hz: +last.hz.toFixed(6),
      actual_v: +last.v.toFixed(6),
      d_hz: +dHz.toFixed(6),
      d_v: +dV.toFixed(6),
    });
    if (Math.abs(dHz) < tolRad && Math.abs(dV) < tolRad) {
      return { state: last, iterations: iter + 1, dHz, dV, converged: true, trace };
    }
    const nextHz = (cmdHz + gain * dHz + 2 * Math.PI) % (2 * Math.PI);
    const nextV = cmdV + gain * dV;
    if (Math.abs(nextHz - cmdHz) < deadZoneRad && Math.abs(nextV - cmdV) < deadZoneRad) {
      break;
    }
    cmdHz = nextHz;
    cmdV = nextV;
  }
  return { state: last, iterations: iter + 1, dHz, dV, converged: false, trace };
};

/* --- Arrow builder ------------------------------------------------- */
const step = (prev, dir, d) => ({
  x_mm: prev.x_mm + (dir === "right" ? d : dir === "left" ? -d : 0),
  y_mm: prev.y_mm + (dir === "up" ? d : dir === "down" ? -d : 0),
});

const DesignPreview = ({ corners, targets, ready }) => {
  if (!corners.length) {
    return (
      <div className="flex h-64 items-center justify-center rounded-md border border-dashed border-[#2A2E39] bg-[#0A0B0E] text-xs text-[#5A6065]">
        Path is empty — start at A (0, 0). Tap an arrow to add the next corner.
      </div>
    );
  }
  const xs = corners.map((c) => c.x_mm), ys = corners.map((c) => c.y_mm);
  const minX = Math.min(0, ...xs), maxX = Math.max(0, ...xs);
  const minY = Math.min(0, ...ys), maxY = Math.max(0, ...ys);
  const w = (maxX - minX) || 1, h = (maxY - minY) || 1;
  const pad = 32, svgW = 480, svgH = 280;
  const scale = Math.min((svgW - 2 * pad) / w, (svgH - 2 * pad) / h);
  const tx = (x) => pad + (x - minX) * scale;
  const ty = (y) => svgH - (pad + (y - minY) * scale);
  return (
    <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full rounded-md border border-[#2A2E39] bg-[#0A0B0E]">
      <polyline points={corners.map((c) => `${tx(c.x_mm)},${ty(c.y_mm)}`).join(" ")}
        fill="none" stroke="#00E1C7" strokeWidth={2} />
      {corners.length >= 3 && (
        <line x1={tx(corners[corners.length - 1].x_mm)} y1={ty(corners[corners.length - 1].y_mm)}
              x2={tx(corners[0].x_mm)} y2={ty(corners[0].y_mm)}
              stroke="#009E8E" strokeDasharray="4 3" strokeWidth={1.5} />
      )}
      {corners.map((c, i) => {
        const t = targets?.[i];
        const fill = i === 0 ? "#00E1C7" : t?.marked ? "#8DC63F" : ready ? "#F5A623" : "#5A6065";
        return (
          <g key={c.id || i}>
            <circle cx={tx(c.x_mm)} cy={ty(c.y_mm)} r={5} fill={fill} />
            <text x={tx(c.x_mm)} y={ty(c.y_mm) - 8}
              fontSize={12} fontFamily="Chakra Petch, sans-serif"
              fill={fill} textAnchor="middle" fontWeight={700}>
              {c.label || CORNER_LABELS[i] || `P${i + 1}`}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

/* --- Live scope Hz readout ---------------------------------------- */
const useLiveScope = () => {
  const [s, setS] = useState({ hz: null, v: null });
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try { const r = await station.state();
        if (!cancelled) setS({ hz: r.hz, v: r.v });
      } catch { /* silent */ }
    };
    tick();
    const id = setInterval(tick, 500);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return s;
};

/* --- Main component ----------------------------------------------- */
export default function SetoutTool({ onBack }) {
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [newName, setNewName] = useState("");
  const [nextDist, setNextDist] = useState("1000");
  const [showBuilder, setShowBuilder] = useState(false);
  const [draft, setDraft] = useState([]);
  const [busy, setBusy] = useState(false);
  const [guided, setGuided] = useState(false);
  const [prismMode] = usePrismMode();
  // Prism pole height, mm. 0 = reflectorless (laser hit direct on ground).
  // Persist across sessions on this device — most crews keep the same pole.
  const [poleHeightMm, setPoleHeightMm] = useState(() => {
    try { return parseFloat(localStorage.getItem("setout.poleHeightMm") || "0") || 0; }
    catch { return 0; }
  });
  const updatePoleHeight = (v) => {
    const n = Number.isFinite(parseFloat(v)) ? Math.max(0, parseFloat(v)) : 0;
    setPoleHeightMm(n);
    try { localStorage.setItem("setout.poleHeightMm", String(n)); } catch { /* silent */ }
  };
  const scope = useLiveScope();

  const refresh = useCallback(async () => {
    try { setSessions(await soApi.list()); } catch { /* silent */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // Set-out ALWAYS marks physical ground points with the visible red
  // laser, so we force the instrument into reflectorless mode the
  // moment this tool opens. Without this, if the surveyor last left
  // the app in prism mode from another tool the TS16 refuses the
  // shot with GRC_AUT_NO_TARGET (rc=8710). Best-effort — silent if
  // the bridge isn't ready yet.
  useEffect(() => {
    station.setTargetType(1).catch(() => { /* silent */ });
  }, []);

  useEffect(() => {
    if (!current) return;
    if (current.design.length === 0) {
      setShowBuilder(true);
      setDraft([{ label: "A", x_mm: 0, y_mm: 0 }]);
    } else if (showBuilder && draft.length === 0) {
      setDraft(current.design.map((c) => ({ label: c.label, x_mm: c.x_mm, y_mm: c.y_mm })));
    } else {
      setShowBuilder(false);
    }
  }, [current?.id, current?.design.length]);

  const create = async () => {
    if (!newName.trim()) return toast.error("Name required");
    const s = await soApi.create({ name: newName.trim() });
    setNewName("");
    await refresh();
    setCurrent(s);
  };

  /* -- arrow builder -------- */
  const addLeg = (dir) => {
    const d = parseFloat(nextDist);
    if (!Number.isFinite(d) || d <= 0) return toast.error("Enter a positive leg length (mm)");
    const prev = draft[draft.length - 1];
    const next = { label: CORNER_LABELS[draft.length] || `P${draft.length + 1}`, ...step(prev, dir, d) };
    setDraft([...draft, next]);
  };
  const undoLeg = () => draft.length > 1 && setDraft(draft.slice(0, -1));
  const resetBuilder = () => {
    if (!window.confirm("Wipe the current path and start again at A?")) return;
    setDraft([{ label: "A", x_mm: 0, y_mm: 0 }]);
  };
  const saveDesign = async () => {
    if (draft.length < 2) return toast.error("Need at least 2 corners (A and B)");
    setBusy(true);
    try {
      const s = await soApi.replaceDesign(current.id, draft);
      setCurrent(s);
      setShowBuilder(false);
      toast.success(`Design saved · ${draft.length} corners`);
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  const editDesign = () => {
    setShowBuilder(true);
    setDraft(current.design.map((c) => ({ label: c.label, x_mm: c.x_mm, y_mm: c.y_mm })));
  };

  /* -- anchor / orient / verify / mark ---------- */
  const shootAnchor = async () => {
    setBusy(true);
    try {
      const m = await station.measureLocal();
      // For reflectorless the laser hits the ground directly so z IS
      // ground level. For a prism-pole shot subtract the pole height
      // so anchor.z represents the ground point (not prism-center).
      const poleM = (poleHeightMm || 0) / 1000.0;
      const groundZ = m.z - poleM;
      const s = await soApi.setAnchor(current.id, {
        x: m.x, y: m.y, z: groundZ,
        hz: m.hz, v: m.v, sd: m.sd,
      });
      setCurrent(s);
      toast.success(
        poleM > 0
          ? `Point A anchored (pole ${poleHeightMm} mm subtracted)`
          : `Point A anchored · x=${m.x.toFixed(3)} y=${m.y.toFixed(3)} z=${m.z.toFixed(3)}`
      );
    } catch (e) { toast.error(e?.message || "Shot failed"); } finally { setBusy(false); }
  };
  const clearAnchor = async () => {
    const s = await soApi.clearAnchor(current.id); setCurrent(s);
  };
  const lockOrientFromScope = async () => {
    // The scope's Hz is the bearing from the STATION to the laser dot
    // — that's only the same as A→B if A sits at the station, which
    // it doesn't. So instead of trusting scope.hz directly, we fire a
    // reflectorless shot at wherever the laser is currently pointing,
    // then let the backend compute the true A→B bearing as
    //   atan2(dot.x − anchor.x, dot.y − anchor.y).
    setBusy(true);
    try {
      const m = await station.measureLocal();
      const s = await soApi.setOrientMeasure(current.id, {
        x: m.x, y: m.y, z: m.z, hz: m.hz, v: m.v, sd: m.sd,
      });
      setCurrent(s);
      toast.success(`A→B direction locked · ${rad2deg(s.orient_hz).toFixed(2)}° (from where the laser was pointing)`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Direction capture failed";
      toast.error(msg);
    } finally { setBusy(false); }
  };
  const shootOrient = async () => {
    setBusy(true);
    try {
      const m = await station.measureLocal();
      const s = await soApi.setOrientMeasure(current.id, {
        x: m.x, y: m.y, z: m.z, hz: m.hz, v: m.v, sd: m.sd,
      });
      setCurrent(s);
      toast.success(`A→B direction locked · ${rad2deg(s.orient_hz).toFixed(2)}° (from Point B shot)`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Shot failed";
      toast.error(msg);
    } finally { setBusy(false); }
  };
  const clearOrient = async () => {
    const s = await soApi.clearOrient(current.id); setCurrent(s);
  };

  // ---- Plane-fit handlers (any-plane set-out) ----------------------
  const addPlaneShot = async () => {
    setBusy(true);
    try {
      const m = await station.measureLocal();
      const s = await soApi.addPlaneShot(current.id, {
        x: m.x, y: m.y, z: m.z, hz: m.hz, v: m.v, sd: m.sd,
      });
      setCurrent(s);
      toast.success(`Plane shot ${s.plane_shots.length}${s.plane_rms_mm != null ? ` · RMS ${s.plane_rms_mm.toFixed(1)} mm` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Plane shot failed");
    } finally { setBusy(false); }
  };
  const deletePlaneShot = async (idx) => {
    setCurrent(await soApi.deletePlaneShot(current.id, idx));
  };
  const togglePlaneMode = async () => {
    const next = current.plane_mode === "fitted" ? "horizontal" : "fitted";
    if (next === "fitted" && (current.plane_shots || []).length < 3) {
      toast.error("Need 3+ plane shots before switching to fitted mode");
      return;
    }
    setCurrent(await soApi.setPlaneMode(current.id, next));
  };
  const slewTo = async (t) => {
    if (t.world_x == null) return;
    try {
      await station.laser(true);
      const { hz, v } = localToAim(t);
      await station.turn({ hz, v });
      toast.success(`Laser on ${t.label}`);
    } catch { toast.error("Slew failed"); }
  };

  // Aim laser at Point A using its recorded anchor coordinates.
  // Handy for double-checking A after you've moved on to B, C, D…
  const aimAtA = async () => {
    if (!current?.anchor_world) return;
    try {
      await station.laser(true);
      const a = current.anchor_world;
      const horiz = Math.hypot(a.x, a.y);
      let hz = Math.atan2(a.x, a.y); if (hz < 0) hz += 2 * Math.PI;
      const v = horiz < 1e-9 && Math.abs(a.z) < 1e-9
        ? Math.PI / 2
        : Math.atan2(horiz, a.z);
      const stateBefore = await station.state().catch(() => null);
      const result = await preciseAim(hz, v);
      soApi.log(current.id, "aim_at_a", {
        anchor: { x: a.x, y: a.y, z: a.z },
        commanded_hz: hz, commanded_v: v,
        scope_before_hz: stateBefore?.hz, scope_before_v: stateBefore?.v,
        scope_after_hz: result.state?.hz, scope_after_v: result.state?.v,
        iterations: result.iterations, converged: result.converged,
        final_d_hz: result.dHz, final_d_v: result.dV,
        trace: result.trace,
      });
      toast.success(
        result.converged
          ? `Laser on Point A · ${result.iterations} passes`
          : `Laser aim at A (best effort · ${result.iterations} passes)`
      );
    } catch (e) { toast.error(e?.message || "Aim failed"); }
  };

  const aimAndVerify = async (t) => {
    if (t.world_x == null) return;
    setBusy(true);
    try {
      await station.laser(true);
      const { hz, v } = localToAim(t);
      const stateBeforeTurn = await station.state().catch(() => null);
      const result = await preciseAim(hz, v);
      soApi.log(current.id, "aim_before_shot", {
        label: t.label, corner_id: t.corner_id,
        target_world: { x: t.world_x, y: t.world_y, z: t.world_z },
        target_actual: { x: t.actual_x, y: t.actual_y, z: t.actual_z },
        commanded_hz: hz, commanded_v: v,
        scope_before_hz: stateBeforeTurn?.hz, scope_before_v: stateBeforeTurn?.v,
        scope_after_hz: result.state?.hz, scope_after_v: result.state?.v,
        iterations: result.iterations, converged: result.converged,
        final_d_hz: result.dHz, final_d_v: result.dV,
        trace: result.trace,
      });
      // small extra settle before the range shot
      await new Promise((r) => setTimeout(r, 200));
      const m = await station.measureLocal();
      const s = await soApi.captureActual(current.id, t.corner_id, {
        x: m.x, y: m.y, z: m.z, hz: m.hz, v: m.v, sd: m.sd,
        commanded_hz: hz, commanded_v: v,
      });
      setCurrent(s);
      const refined = s.targets.find((x) => x.corner_id === t.corner_id);
      if (refined && refined.hz != null) {
        // Re-precise-aim to the refined (post-actual-z) point so laser
        // lands exactly on the true ground point for the operator to mark.
        try { await preciseAim(refined.hz, refined.v); } catch { /* silent */ }
      }
      const residual = refined?.verify_residual_mm;
      const converge = result.converged ? "" : ` · aim drifted ${(Math.max(Math.abs(result.dHz || 0), Math.abs(result.dV || 0)) * 180 / Math.PI).toFixed(2)}°`;
      if (residual != null) {
        const good = residual <= s.tolerance_mm;
        toast[good ? "success" : "error"](
          `${t.label} · Δ ${residual.toFixed(1)} mm vs design ${good ? "✓" : "✗ tol " + s.tolerance_mm + " mm"}${converge}`
        );
      } else {
        toast.success(`${t.label} · reading captured${converge}`);
      }
    } catch (e) {
      toast.error(e?.message || "Reading failed");
    } finally { setBusy(false); }
  };

  // ---- Iterative walk-in ---------------------------------------------------
  //
  // The regular aimAndVerify only refines Z after the shot — any XY drift
  // (station-rotation guess error, EDM slope, atmospheric refraction) stays
  // as an uncorrected residual and the operator marks wherever the laser
  // happened to land. This walk-in loop closes that error loop:
  //
  //   1. Aim at the current best guess (design x/y, best-known ground z).
  //   2. Fire → get the actual landing point (x0, y0, z0).
  //   3. If |design − actual| in XY is below tolerance we're done.
  //   4. Otherwise recompute Hz/V using the design XY but z0 as the ground
  //      elevation at the intended spot, and re-aim + re-fire.
  //   5. Converges in 1-3 iterations on any reasonably flat surface.
  //
  // Uses the same closed-loop `preciseAim` for each aim step so angular
  // pointing stays sub-arcsec at every iteration.
  const walkIn = async (t, { maxIter = 5, extraSettleMs = 250 } = {}) => {
    if (t.world_x == null) return;
    setBusy(true);
    const startedAt = Date.now();
    try {
      await station.laser(true);
      const stationSnap = await station.state();
      const sx = stationSnap.station_x || 0;
      const sy = stationSnap.station_y || 0;
      const sz = (stationSnap.station_z || 0) + (stationSnap.instrument_height || 0);
      const dx = t.world_x - sx;
      const dy = t.world_y - sy;
      let zGround = t.actual_z != null ? t.actual_z : t.world_z;

      let bestResidual = null;
      let bestShot = null;
      let iter = 0;

      for (iter = 0; iter < maxIter; iter++) {
        // Recompute aim from station → (design_x, design_y, current z-guess)
        const dz = zGround - sz;
        const horiz = Math.hypot(dx, dy);
        let hz = Math.atan2(dx, dy);
        if (hz < 0) hz += 2 * Math.PI;
        const v = Math.atan2(horiz, dz);

        const aim = await preciseAim(hz, v);
        await new Promise((r) => setTimeout(r, extraSettleMs));
        const m = await station.measureLocal();

        // Residual in the XY plane only — Z is a free parameter of the
        // ground, we can only chase XY.
        const rx = m.x - t.world_x;
        const ry = m.y - t.world_y;
        const rxy_mm = Math.hypot(rx, ry) * 1000;
        bestShot = { m, aim, hz, v };
        bestResidual = rxy_mm;

        soApi.log(current.id, "walk_in_iter", {
          label: t.label, corner_id: t.corner_id, iter: iter + 1,
          commanded_hz: hz, commanded_v: v,
          shot_x: m.x, shot_y: m.y, shot_z: m.z,
          design_x: t.world_x, design_y: t.world_y,
          residual_xy_mm: +rxy_mm.toFixed(3),
        }).catch(() => undefined);

        if (rxy_mm <= current.tolerance_mm) {
          // Converged in XY. Before we capture the actual, snap the
          // crosshair onto the prism centre with AUT_FineAdjust so the
          // final Hz/V we record is dead-on the prism (not 1″ off from
          // the AUT_MakePositioning dead-zone). Only meaningful in
          // Prism mode — reflectorless has no ATR target to snap onto.
          let finalM = m;
          let finalHz = hz;
          let finalV = v;
          let snapped = false;
          if (prismMode) {
            try {
              await station.fineAdjust();
              await new Promise((r) => setTimeout(r, 200));
              const m2 = await station.measureLocal();
              const rx2 = m2.x - t.world_x;
              const ry2 = m2.y - t.world_y;
              const rxy2_mm = Math.hypot(rx2, ry2) * 1000;
              // Accept the fine-adjust shot only if it didn't kick us
              // outside tolerance (ATR sometimes locks onto a wrong
              // reflector in messy environments — protect the walk-in
              // result).
              if (rxy2_mm <= current.tolerance_mm) {
                finalM = m2;
                snapped = true;
                const state2 = await station.state().catch(() => null);
                if (state2 && state2.hz != null && state2.v != null) {
                  finalHz = state2.hz;
                  finalV = state2.v;
                }
                soApi.log(current.id, "fine_adjust_snap", {
                  label: t.label, corner_id: t.corner_id,
                  residual_before_mm: +rxy_mm.toFixed(3),
                  residual_after_mm: +rxy2_mm.toFixed(3),
                  final_hz: finalHz, final_v: finalV,
                }).catch(() => undefined);
              } else {
                soApi.log(current.id, "fine_adjust_rejected", {
                  label: t.label, corner_id: t.corner_id,
                  residual_before_mm: +rxy_mm.toFixed(3),
                  residual_after_mm: +rxy2_mm.toFixed(3),
                }).catch(() => undefined);
              }
            } catch (e) {
              // Fine-adjust failed (no prism in window / RPC error) —
              // fall back to the walk-in shot silently. The residual
              // was already inside tolerance so we haven't lost anything.
              soApi.log(current.id, "fine_adjust_failed", {
                label: t.label, corner_id: t.corner_id,
                error: e?.message || String(e),
              }).catch(() => undefined);
            }
          }
          // Converged — persist this shot as the actual.
          const s = await soApi.captureActual(current.id, t.corner_id, {
            x: finalM.x, y: finalM.y, z: finalM.z,
            hz: finalM.hz, v: finalM.v, sd: finalM.sd,
            commanded_hz: finalHz, commanded_v: finalV,
          });
          setCurrent(s);
          const finalResidualMm = Math.hypot(finalM.x - t.world_x, finalM.y - t.world_y) * 1000;
          toast.success(
            `${t.label} · WALKED IN · Δxy ${finalResidualMm.toFixed(1)} mm · ${iter + 1} passes${snapped ? " · fine-snap ✓" : ""} · ${(Date.now() - startedAt) / 1000}s`
          );
          return;
        }
        // Not converged — use this shot's z as the new ground guess and loop.
        zGround = m.z;
      }

      // Fell out of the loop without converging — persist the best shot,
      // report the residual, and let the operator decide.
      if (bestShot) {
        const s = await soApi.captureActual(current.id, t.corner_id, {
          x: bestShot.m.x, y: bestShot.m.y, z: bestShot.m.z,
          hz: bestShot.m.hz, v: bestShot.m.v, sd: bestShot.m.sd,
          commanded_hz: bestShot.hz, commanded_v: bestShot.v,
        });
        setCurrent(s);
      }
      toast.error(
        `${t.label} · did not converge after ${iter} passes · Δxy ${bestResidual?.toFixed(1) ?? "?"} mm`
      );
    } catch (e) {
      toast.error(e?.message || "Walk-in failed");
    } finally { setBusy(false); }
  };
  const toggleMarked = async (t) => {
    try {
      const s = await soApi.markTarget(current.id, t.corner_id, !t.marked);
      setCurrent(s);
    } catch { /* silent */ }
  };
  const captureActual = async (t) => {
    // Legacy path kept for the corners-table row buttons if ever needed.
    return aimAndVerify(t);
  };
  const clearActual = async (t) => {
    try {
      const s = await soApi.clearActual(current.id, t.corner_id);
      setCurrent(s);
    } catch { /* silent */ }
  };

  const verifyTarget = async (t) => {
    setBusy(true);
    try {
      const m = await station.measureLocal();
      const s = await soApi.verifyTarget(current.id, t.corner_id, { x: m.x, y: m.y, z: m.z });
      setCurrent(s);
      const tt = s.targets.find((x) => x.corner_id === t.corner_id);
      const good = tt.verify_residual_mm <= s.tolerance_mm;
      if (good) toast.success(`${t.label} verified · Δ ${tt.verify_residual_mm.toFixed(1)} mm ✓`);
      else toast.error(`${t.label} off by ${tt.verify_residual_mm.toFixed(1)} mm > tol ${s.tolerance_mm} mm`);
    } catch (e) { toast.error(e?.message || "Shot failed"); } finally { setBusy(false); }
  };

  const anchorSet = !!current?.anchor_world;
  const orientSet = current?.orient_hz != null;
  const ready = !!current?.ready;
  const activeTarget = useMemo(() => {
    if (!current?.ready) return null;
    return current.targets.slice(1).find((t) => !t.marked) || null;
  }, [current]);

  /* -- session list -- */
  if (!current) {
    return (
      <div className="min-h-screen bg-[#0A0B0E] text-white" data-testid="setout-page">
        <div className="mx-auto max-w-4xl px-4 py-4 space-y-4">
          <div className="flex items-center justify-between">
            <button onClick={onBack} data-testid="setout-back"
              className="flex items-center gap-1 rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-1.5 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E]">
              <ArrowLeft size={12} /> Toolbox
            </button>
            <span className="text-[10px] uppercase tracking-widest text-[#8B939A]">Ground point set-out</span>
          </div>
          <div className="rounded-md border border-[#2A2E39] bg-[#14161A] p-4">
            <div className="grid gap-3 sm:grid-cols-[1fr,140px]">
              <input data-testid="setout-new-name" value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Set-out ID (e.g. Pad footprint E-3)"
                className="rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white focus:border-[#009E8E] focus:outline-none" />
              <button onClick={create} data-testid="setout-create"
                className="flex items-center justify-center gap-2 rounded-sm border border-[#009E8E] bg-[#009E8E]/10 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-[#009E8E] hover:bg-[#009E8E]/20">
                <Plus size={12} strokeWidth={2.5} /> Create
              </button>
            </div>
            <p className="mt-2 text-[10px] text-[#5A6065]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              Workflow: draw shape with arrows → shoot A → aim laser at B → auto-project every remaining corner.
            </p>
          </div>
          <div className="space-y-2">
            {sessions.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-md border border-[#2A2E39] bg-[#14161A] p-3 hover:border-[#009E8E]">
                <button className="flex-1 text-left" onClick={async () => setCurrent(await soApi.get(s.id))} data-testid={`setout-open-${s.id}`}>
                  <div className="font-bold text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>{s.name}</div>
                  <div className="mt-1 text-[10px] text-[#8B939A]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    {s.n_corners} corners · {s.anchor_set ? "A✓" : "A—"} · {s.orient_set ? "dir✓" : "dir—"} · {s.n_marked} marked · perim {s.perimeter_mm.toFixed(0)} mm
                  </div>
                </button>
                <button onClick={async () => { await soApi.del(s.id); refresh(); }}
                  className="rounded-sm border border-transparent p-1.5 text-[#5A6065] hover:border-[#FF3B30] hover:text-[#FF3B30]">
                  <Trash size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* -- session view -- */
  if (guided) {
    return (
      <SetoutGuided
        current={current}
        soApi={soApi}
        setCurrent={setCurrent}
        tolerance_mm={current.tolerance_mm}
        onExit={() => setGuided(false)}
      />
    );
  }
  return (
    <ToolSplitLayout
      data-testid="setout-session"
      title={current.name}
      subtitle={
        ready
          ? `Ready · ${current.targets.length} corners · ${current.targets.filter((t) => t.marked).length} marked · perim ${current.perimeter_mm.toFixed(0)} mm`
          : showBuilder
            ? `1. Design · ${draft.length} corner${draft.length !== 1 ? "s" : ""} drawn`
            : anchorSet && !orientSet
              ? "4. Point laser at B, then Lock A→B direction"
              : !anchorSet
                ? "3. Shoot Point A with prism"
                : "Design incomplete"
      }
      left={
        <div className="space-y-4">
          <PrintReportShell
            toolName="Set-Out"
            sessionName={current.name}
            passFail={ready && current.targets.length > 0
              ? (current.targets.every((t) => t.marked) ? "PASS" : null)
              : null}
            headline={ready
              ? `${current.targets.filter((t) => t.marked).length}/${current.targets.length} corners marked · perim ${current.perimeter_mm.toFixed(0)} mm`
              : "Design incomplete"
            }
            surveyor="—"
          >
          {ready && (
            <div className="flex items-center justify-end gap-2 no-print">
              <PrintReportButton testid="setout-print-btn" />
            </div>
          )}
          {showBuilder ? (
            /* --- Arrow builder ------------------------------------- */
            <div className="rounded-md border border-[#00E1C7]/30 bg-[#00E1C7]/5 p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-[#00E1C7]">
                  1. Draw the shape · start = A (0, 0)
                </div>
                <button data-testid="setout-builder-reset" onClick={resetBuilder}
                  className="rounded-sm border border-[#FF3B30]/40 px-2 py-1 text-[10px] uppercase tracking-widest text-[#FF3B30] hover:bg-[#FF3B30]/10">
                  <X size={12} className="inline" /> Reset
                </button>
              </div>
              <DesignPreview corners={draft} targets={[]} ready={false} />
              <div className="mt-3 grid gap-3 sm:grid-cols-[220px,1fr]">
                <div className="mx-auto grid w-[220px] grid-cols-3 grid-rows-3 gap-1">
                  <div />
                  <button data-testid="setout-arrow-up" onClick={() => addLeg("up")}
                    className="flex items-center justify-center rounded-md border border-[#00E1C7]/60 bg-[#00E1C7]/10 py-3 text-[#00E1C7] hover:bg-[#00E1C7]/20 active:scale-95">
                    <ArrowUp size={22} strokeWidth={2.5} /></button>
                  <div />
                  <button data-testid="setout-arrow-left" onClick={() => addLeg("left")}
                    className="flex items-center justify-center rounded-md border border-[#00E1C7]/60 bg-[#00E1C7]/10 py-3 text-[#00E1C7] hover:bg-[#00E1C7]/20 active:scale-95">
                    <ArrowLeft size={22} strokeWidth={2.5} /></button>
                  <button data-testid="setout-builder-undo" onClick={undoLeg} disabled={draft.length <= 1}
                    className="flex items-center justify-center rounded-md border border-[#F5A623]/40 bg-[#F5A623]/10 py-3 text-[#F5A623] hover:bg-[#F5A623]/20 disabled:opacity-30">
                    <Undo2 size={18} strokeWidth={2.5} /></button>
                  <button data-testid="setout-arrow-right" onClick={() => addLeg("right")}
                    className="flex items-center justify-center rounded-md border border-[#00E1C7]/60 bg-[#00E1C7]/10 py-3 text-[#00E1C7] hover:bg-[#00E1C7]/20 active:scale-95">
                    <ArrowRight size={22} strokeWidth={2.5} /></button>
                  <div />
                  <button data-testid="setout-arrow-down" onClick={() => addLeg("down")}
                    className="flex items-center justify-center rounded-md border border-[#00E1C7]/60 bg-[#00E1C7]/10 py-3 text-[#00E1C7] hover:bg-[#00E1C7]/20 active:scale-95">
                    <ArrowDown size={22} strokeWidth={2.5} /></button>
                  <div />
                </div>
                <div className="space-y-2">
                  <label className="block text-[10px] uppercase tracking-widest text-[#8B939A]">
                    Next leg length (mm)
                    <input type="number" step="1" min="1" data-testid="setout-builder-dist"
                      value={nextDist} onChange={(e) => setNextDist(e.target.value)}
                      className="mt-1 w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 text-center font-mono text-lg text-white focus:border-[#00E1C7] focus:outline-none" />
                  </label>
                  <div className="grid grid-cols-3 gap-1 text-[10px]">
                    {[100, 500, 1000, 2000, 5000, 10000].map((v) => (
                      <button key={v} onClick={() => setNextDist(String(v))}
                        data-testid={`setout-preset-dist-${v}`}
                        className="rounded-sm border border-[#2A2E39] bg-[#14161A] py-1 font-mono text-[#8B939A] hover:border-[#00E1C7] hover:text-[#00E1C7]">
                        {v} mm
                      </button>
                    ))}
                  </div>
                  <button data-testid="setout-save-design" onClick={saveDesign}
                    disabled={busy || draft.length < 2}
                    className="w-full rounded-md border border-[#8DC63F] bg-[#8DC63F]/10 py-3 text-sm font-black uppercase tracking-widest text-[#8DC63F] hover:bg-[#8DC63F]/20 disabled:opacity-40"
                    style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                    Save design ({draft.length} corners)
                  </button>
                </div>
              </div>
              <p className="mt-2 text-[10px] text-[#5A6065]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                ↑ = +Y, ↓ = −Y, → = +X, ← = −X · A is fixed at (0, 0) · rotation to world comes from A→B direction later.
              </p>
            </div>
          ) : (
            <>
              <DesignPreview corners={current.design} targets={current.targets} ready={ready} />
              <button data-testid="setout-edit-shape" onClick={editDesign}
                className="rounded-sm border border-[#009E8E] bg-[#009E8E]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#009E8E] hover:bg-[#009E8E]/20">
                Edit design
              </button>

              {/* Corners table */}
              <div className="overflow-hidden rounded-md border border-[#2A2E39] bg-[#14161A]">
                <table className="w-full text-[11px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                  <thead>
                    <tr className="border-b border-[#2A2E39] bg-[#0A0B0E] text-[#8B939A]">
                      <th className="p-2 text-left">#</th>
                      <th className="p-2 text-left">Label</th>
                      <th className="p-2 text-right">Design X (mm)</th>
                      <th className="p-2 text-right">Design Y (mm)</th>
                      <th className="p-2 text-right">Aim Hz</th>
                      <th className="p-2 text-right">Verify Δ</th>
                      <th className="p-2 text-center">State</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody className="text-[#B5BEC5]">
                    {current.design.map((c, i) => {
                      const t = current.targets[i];
                      const badRes = t?.verify_residual_mm != null && t.verify_residual_mm > current.tolerance_mm;
                      return (
                        <tr key={c.id} className={`border-b border-[#1A1D24] ${activeTarget && activeTarget.corner_id === c.id ? "bg-[#F5A623]/5" : ""}`}>
                          <td className="p-2">#{i + 1}</td>
                          <td className="p-2 text-white">{c.label}</td>
                          <td className="p-2 text-right">{c.x_mm.toFixed(0)}</td>
                          <td className="p-2 text-right">{c.y_mm.toFixed(0)}</td>
                          <td className="p-2 text-right">{t?.hz != null ? rad2deg(t.hz).toFixed(2) + "°" : "—"}</td>
                          <td className={`p-2 text-right ${badRes ? "text-[#FF3B30] font-bold" : "text-[#8DC63F]"}`}>
                            {t?.verify_residual_mm != null ? t.verify_residual_mm.toFixed(1) : "—"}
                          </td>
                          <td className="p-2 text-center">
                            {i === 0 && anchorSet ? (
                              <span className="rounded-sm bg-[#00E1C7]/20 px-2 py-0.5 text-[9px] uppercase tracking-widest text-[#00E1C7]">ANCHOR</span>
                            ) : t?.marked ? (
                              <span className="rounded-sm bg-[#8DC63F]/20 px-2 py-0.5 text-[9px] uppercase tracking-widest text-[#8DC63F]">MARKED</span>
                            ) : ready ? (
                              <span className="rounded-sm bg-[#F5A623]/20 px-2 py-0.5 text-[9px] uppercase tracking-widest text-[#F5A623]">TO SET</span>
                            ) : (
                              <span className="rounded-sm bg-[#2A2E39] px-2 py-0.5 text-[9px] uppercase tracking-widest text-[#8B939A]">—</span>
                            )}
                          </td>
                          <td className="p-2">
                            <div className="flex justify-end gap-1">
                              {i === 0 && anchorSet && (
                                <button data-testid={`setout-aim-a`} onClick={aimAtA}
                                  className="rounded-sm border border-[#00E1C7]/60 p-1 text-[#00E1C7] hover:bg-[#00E1C7]/10" title="Re-aim laser at A (recorded anchor)">
                                  <Aperture size={12} />
                                </button>
                              )}
                              {ready && i > 0 && (
                                <>
                                  <button data-testid={`setout-slew-${c.id}`} onClick={() => aimAndVerify(t)}
                                    className="rounded-sm border border-[#00E1C7]/60 p-1 text-[#00E1C7] hover:bg-[#00E1C7]/10" title="Aim + take reading + auto-adjust for ground level">
                                    <Aperture size={12} />
                                  </button>
                                  <button data-testid={`setout-walkin-${c.id}`} onClick={() => walkIn(t)}
                                    className="rounded-sm border border-[#F5A623]/70 p-1 text-[#F5A623] hover:bg-[#F5A623]/10" title="Walk-in — iterate the laser onto the design point until |Δxy| < tolerance">
                                    <TargetIcon size={12} />
                                  </button>
                                  <button data-testid={`setout-verify-${c.id}`} onClick={() => toggleMarked(t)}
                                    className={`rounded-sm border p-1 ${t.marked ? "border-[#8DC63F] text-[#8DC63F]" : "border-[#2A2E39] text-[#5A6065] hover:border-[#8DC63F] hover:text-[#8DC63F]"}`} title={t.marked ? "Unmark" : "Confirm marked"}>
                                    <CheckCircle2 size={12} />
                                  </button>
                                </>
                              )}
                              {ready && (
                                <button data-testid={`setout-mark-${c.id}`} onClick={() => toggleMarked(t)}
                                  className={`rounded-sm border p-1 ${t.marked ? "border-[#8DC63F] text-[#8DC63F]" : "border-[#2A2E39] text-[#5A6065] hover:border-[#8DC63F] hover:text-[#8DC63F]"}`} title="Toggle marked">
                                  <Flag size={12} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center gap-2 text-[11px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                <label className="flex items-center gap-1 text-[#8B939A]">
                  Tolerance (mm)
                  <input type="number" step="0.5" min="0.5" data-testid="setout-tol"
                    value={current.tolerance_mm}
                    onChange={async (e) => {
                      const v = parseFloat(e.target.value) || 5;
                      setCurrent(await soApi.tol(current.id, v));
                    }}
                    className="w-16 rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-2 py-0.5 text-center text-white" />
                </label>
              </div>
            </>
          )}
          </PrintReportShell>
        </div>
      }
      right={
        <>
          <div className="flex items-center gap-2">
            <button onClick={() => setCurrent(null)} data-testid="setout-back-session"
              className="flex-1 rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E]">
              ← Sessions
            </button>
            <button onClick={() => setGuided(true)} data-testid="setout-guided-enter"
              className="flex-1 rounded-md border border-[#F5A623] bg-[#F5A623]/15 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-[#F5A623] hover:bg-[#F5A623]/25"
              title="Prism-lead single-screen wizard with auto-shot on stop and beep guidance">
              ⚡ Guided mode
            </button>
          </div>

          {/* live scope */}
          {(anchorSet && !orientSet) && (
            <div className="rounded-sm border border-[#F5A623]/40 bg-[#F5A623]/5 px-2 py-1 text-[10px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              <span className="text-[#8B939A]">Scope: </span>
              <span className="text-[#F5A623] font-bold">Hz {scope.hz == null ? "—" : rad2deg(scope.hz).toFixed(2) + "°"}</span>
              <span className="text-[#8B939A]"> · V {scope.v == null ? "—" : rad2deg(scope.v).toFixed(2) + "°"}</span>
            </div>
          )}

          {/* Plane panel — any-plane set-out. Establish FIRST (before
              anchor / orient) so Point A and every subsequent aim
              respects the surface tilt. Optional: leave in horizontal
              mode and the tool behaves like before. */}
          {!showBuilder && current.design.length >= 2 && !anchorSet && (
            <div className="rounded-md border border-[#F5A623]/40 bg-[#F5A623]/5 p-3 space-y-2" data-testid="setout-plane-panel">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-[#F5A623]">
                  2. Plane
                  <span className={`ml-2 rounded-sm px-1.5 py-0.5 text-[9px] ${current.plane_mode === "fitted" ? "bg-[#F5A623]/20 text-[#F5A623]" : "bg-[#2A2E39] text-[#8B939A]"}`}>
                    {current.plane_mode === "fitted" ? `FITTED · ${(current.plane_shots || []).length} shots` : "HORIZONTAL"}
                  </span>
                </div>
                <button data-testid="setout-plane-toggle" onClick={togglePlaneMode}
                  className="rounded-sm border border-[#2A2E39] px-2 py-0.5 text-[9px] uppercase tracking-widest text-[#8B939A] hover:border-[#F5A623] hover:text-[#F5A623]">
                  {current.plane_mode === "fitted" ? "→ horizontal" : "→ fitted"}
                </button>
              </div>
              {current.plane_mode === "fitted" && (current.plane_tilt_deg != null) && (
                <div className="text-[10px] font-mono text-[#8DC63F]">
                  ✓ Tilt {current.plane_tilt_deg.toFixed(2)}° · RMS {current.plane_rms_mm?.toFixed(1) ?? "—"} mm
                </div>
              )}
              {(current.plane_shots || []).length > 0 && (
                <div className="max-h-24 overflow-y-auto rounded-sm border border-[#1A1D24] bg-[#0A0B0E] p-1">
                  {current.plane_shots.map((p, i) => (
                    <div key={i} className="flex items-center justify-between border-b border-[#1A1D24] py-0.5 text-[10px] font-mono text-[#8B939A]">
                      <span>P{i + 1}</span>
                      <span className="text-[#4A5054]">{p.x.toFixed(3)}, {p.y.toFixed(3)}, {p.z.toFixed(3)}</span>
                      <button data-testid={`setout-plane-del-${i}`} onClick={() => deletePlaneShot(i)}
                        className="text-[#FF3B30] hover:text-[#FF6B60]"><X size={10} /></button>
                    </div>
                  ))}
                </div>
              )}
              <button data-testid="setout-plane-addshot" onClick={addPlaneShot} disabled={busy}
                className="w-full rounded-sm border border-[#F5A623] bg-[#F5A623]/15 py-1.5 text-[11px] font-bold uppercase tracking-widest text-[#F5A623] disabled:opacity-40">
                <Plus size={12} className="mr-1 inline" strokeWidth={2.5} />
                Add plane shot ({(current.plane_shots || []).length}/3+)
              </button>
              <p className="text-[9px] leading-tight text-[#4A5054]">
                Fire 3+ reflectorless shots anywhere on the target surface — deck, wall, sloped slab. Skip if you're setting out on level ground.
              </p>
            </div>
          )}

          {/* Phase 2: shoot A */}
          {!showBuilder && current.design.length >= 2 && !anchorSet && (
            <div className="space-y-2 rounded-md border border-[#00E1C7]/30 bg-[#00E1C7]/5 p-3">
              <details>
                <summary className="cursor-pointer text-[10px] uppercase tracking-widest text-[#8B939A] hover:text-[#00E1C7]">
                  Advanced · prism pole height (mm)  · currently {poleHeightMm} = {poleHeightMm === 0 ? "reflectorless" : `${poleHeightMm} mm pole`}
                </summary>
                <div className="mt-2 space-y-2">
                  <input type="number" step="1" min="0" data-testid="setout-pole-height"
                    value={poleHeightMm}
                    onChange={(e) => updatePoleHeight(e.target.value)}
                    className="w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 text-center font-mono text-lg text-white focus:border-[#00E1C7] focus:outline-none" />
                  <div className="grid grid-cols-4 gap-1 text-[10px]">
                    {[0, 1500, 1800, 2000].map((v) => (
                      <button key={v} onClick={() => updatePoleHeight(v)}
                        data-testid={`setout-pole-preset-${v}`}
                        className={`rounded-sm border py-1 font-mono ${poleHeightMm === v ? "border-[#00E1C7] text-[#00E1C7]" : "border-[#2A2E39] text-[#8B939A] hover:border-[#00E1C7] hover:text-[#00E1C7]"}`}>
                        {v === 0 ? "REFL" : `${v}`}
                      </button>
                    ))}
                  </div>
                </div>
              </details>
              <button data-testid="setout-shoot-a" disabled={busy} onClick={shootAnchor}
                className="min-h-[72px] w-full rounded-md border border-[#00E1C7] bg-gradient-to-br from-[#00E1C7]/20 to-[#005a52]/20 py-4 text-lg font-black uppercase tracking-widest text-[#00E1C7] disabled:opacity-40"
                style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                <div className="flex items-center justify-center gap-2">
                  <Crosshair size={22} strokeWidth={2.5} />
                  Shoot Point A
                </div>
              </button>
            </div>
          )}
          {!showBuilder && anchorSet && (
            <div className="space-y-1">
              <div className="rounded-sm border border-[#00E1C7]/40 bg-[#00E1C7]/5 p-2 text-[10px] flex items-center justify-between">
                <span className="text-[#00E1C7]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                  ✓ A @ ({current.anchor_world.x.toFixed(3)}, {current.anchor_world.y.toFixed(3)}, {current.anchor_world.z.toFixed(3)})
                </span>
                <button onClick={clearAnchor} className="text-[#5A6065] hover:text-[#FF3B30]"><RotateCcw size={10} /></button>
              </div>
              <button data-testid="setout-aim-a-panel" onClick={aimAtA}
                className="w-full rounded-sm border border-[#00E1C7]/60 bg-[#00E1C7]/10 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#00E1C7] hover:bg-[#00E1C7]/20"
                style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                <Aperture size={11} className="mr-1 inline" strokeWidth={2.5} /> Aim laser at Point A
              </button>
            </div>
          )}

          {/* Phase 3: lock direction */}
          {!showBuilder && anchorSet && !orientSet && (
            <div className="rounded-md border border-[#F5A623]/40 bg-[#F5A623]/5 p-3 space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-[#F5A623]">
                Point the laser (or move the prism) in the direction of B
              </div>
              <button data-testid="setout-lock-orient" disabled={busy} onClick={lockOrientFromScope}
                className="min-h-[60px] w-full rounded-md border border-[#F5A623] bg-gradient-to-br from-[#F5A623]/20 to-[#8B5A00]/20 py-3 text-sm font-black uppercase tracking-widest text-[#F5A623] disabled:opacity-40"
                style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                <div className="flex items-center justify-center gap-2">
                  <Aperture size={18} strokeWidth={2.5} />
                  Lock A→B direction from laser
                </div>
              </button>
              <div className="text-center text-[10px] text-[#5A6065]">— or —</div>
              <button data-testid="setout-shoot-b" disabled={busy} onClick={shootOrient}
                className="min-h-[52px] w-full rounded-md border border-[#00E1C7] bg-gradient-to-br from-[#00E1C7]/20 to-[#005a52]/20 py-3 text-sm font-black uppercase tracking-widest text-[#00E1C7] disabled:opacity-40"
                style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                <div className="flex items-center justify-center gap-2">
                  <Crosshair size={16} strokeWidth={2.5} />
                  Shoot Point B with prism
                </div>
              </button>
            </div>
          )}
          {!showBuilder && orientSet && (
            <div className="rounded-sm border border-[#8DC63F]/40 bg-[#8DC63F]/5 p-2 text-[10px] flex items-center justify-between">
              <span className="text-[#8DC63F]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                ✓ A→B direction {rad2deg(current.orient_hz).toFixed(2)}° ({current.orient_source})
              </span>
              <button onClick={clearOrient} className="text-[#5A6065] hover:text-[#FF3B30]"><RotateCcw size={10} /></button>
            </div>
          )}

          {/* Plane panel — any-plane set-out. Optional; if left in
              horizontal mode the tool behaves like before. */}
          {!showBuilder && orientSet && (current.plane_mode === "fitted" || (current.plane_shots || []).length > 0) && (
            <div className="rounded-md border border-[#2A2E39] bg-[#0F1116] p-2 space-y-2" data-testid="setout-plane-panel-status">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-[#8B939A]">
                  Plane
                  <span className={`ml-2 rounded-sm px-1.5 py-0.5 text-[9px] ${current.plane_mode === "fitted" ? "bg-[#F5A623]/20 text-[#F5A623]" : "bg-[#2A2E39] text-[#8B939A]"}`}>
                    {current.plane_mode === "fitted" ? `FITTED · ${current.plane_shots.length} shots` : "HORIZONTAL"}
                  </span>
                </div>
                <button data-testid="setout-plane-toggle" onClick={togglePlaneMode}
                  className="rounded-sm border border-[#2A2E39] px-2 py-0.5 text-[9px] uppercase tracking-widest text-[#8B939A] hover:border-[#F5A623] hover:text-[#F5A623]">
                  {current.plane_mode === "fitted" ? "→ horizontal" : "→ fitted"}
                </button>
              </div>
              {current.plane_mode === "fitted" && (current.plane_tilt_deg != null) && (
                <div className="text-[10px] font-mono text-[#8B939A]">
                  Tilt {current.plane_tilt_deg.toFixed(2)}° · RMS {current.plane_rms_mm?.toFixed(1) ?? "—"} mm
                </div>
              )}
            </div>
          )}

          {/* Phase 4: laser + verify next unmarked */}
          {ready && activeTarget && (
            <div className="rounded-md border border-[#00E1C7]/30 bg-[#00E1C7]/5 p-3 space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-[#00E1C7]">
                Next: {activeTarget.label} at {rad2deg(activeTarget.hz).toFixed(2)}° Hz
                {activeTarget.verify_residual_mm != null && (
                  <span className={`ml-2 rounded-sm px-1.5 py-0.5 text-[9px] ${activeTarget.verify_residual_mm <= current.tolerance_mm ? "bg-[#8DC63F]/20 text-[#8DC63F]" : "bg-[#FF3B30]/20 text-[#FF3B30]"}`}>
                    Δ {activeTarget.verify_residual_mm.toFixed(1)} mm
                  </span>
                )}
              </div>
              <button data-testid="setout-slew-active" disabled={busy} onClick={() => aimAndVerify(activeTarget)}
                className="min-h-[60px] w-full rounded-md border border-[#00E1C7] bg-gradient-to-br from-[#00E1C7]/20 to-[#005a52]/20 py-3 text-sm font-black uppercase tracking-widest text-[#00E1C7] disabled:opacity-40"
                style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                <div className="flex items-center justify-center gap-2">
                  <Aperture size={18} strokeWidth={2.5} />
                  Aim laser at {activeTarget.label} + verify
                </div>
              </button>
              <button data-testid="setout-walkin-active" disabled={busy} onClick={() => walkIn(activeTarget)}
                className="min-h-[52px] w-full rounded-md border border-[#F5A623] bg-gradient-to-br from-[#F5A623]/20 to-[#7a5310]/20 py-3 text-sm font-black uppercase tracking-widest text-[#F5A623] disabled:opacity-40"
                style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                <div className="flex items-center justify-center gap-2">
                  <TargetIcon size={16} strokeWidth={2.5} />
                  Walk in {activeTarget.label} · Δxy ≤ {current.tolerance_mm} mm
                </div>
              </button>
              <p className="text-[10px] text-[#5A6065] text-center" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                Slew → reflectorless shot → auto-adjust for ground level → re-aim laser<br/>
                Walk-in loops fire→adjust→fire until XY residual is under tolerance
              </p>
              <button data-testid="setout-verify-active" disabled={busy} onClick={() => toggleMarked(activeTarget)}
                className="min-h-[52px] w-full rounded-md border border-[#8DC63F] bg-gradient-to-br from-[#8DC63F]/20 to-[#3d5c1a]/20 py-3 text-sm font-black uppercase tracking-widest text-[#8DC63F] disabled:opacity-40"
                style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                <div className="flex items-center justify-center gap-2">
                  <CheckCircle2 size={16} strokeWidth={2.5} />
                  Confirm {activeTarget.label} marked
                </div>
              </button>
            </div>
          )}

          {ready && !activeTarget && (
            <div className="rounded-sm border border-[#8DC63F]/40 bg-[#8DC63F]/5 p-2 text-center text-[10px] uppercase tracking-widest text-[#8DC63F]" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
              All corners marked ✓
            </div>
          )}

          <AimPad tool="setout" sessionId={current.id} />
        </>
      }
    />
  );
}
